import { useEffect, FC } from 'react';
import moment from 'moment';
import dayjs from 'dayjs';
import { Icons } from '@assets';
import { useDispatch, useSelector } from 'react-redux';
import { authInfoSelect } from '@entities/auth';
import { API_DATE_FORMAT, DISPLAY_DATE_FORMAT } from '@utils/const';
import { rKeeperSelect } from '@entities/rKeeper';
import { AppDispatch } from '@config/store';
import TableRevenueRestaurants from '../components/revenue/TableRevenueRetaurants';
import {
    getRestaurants,
    getRestTotal,
    getRevenueRestaurants,
    getRevenueWaiters,
} from '@api/thunks/rKeeper';
import TableHeaderRevenue from '../components/revenue/TableHeaderRevenue';
import { LineChartRestaurants } from '../components/revenue/LineChartRestaurants';
import HeaderRestaurantsInfo from '../components/revenue/HeaderRestaurantsInfo';
import { IRestTotalReq } from '@api/services/rKeeperApi/types';
import TableRevenueWaiters from '../components/revenue/TableRevenueWaiters';
import { LineChartWaiters } from '../components/revenue/LineChartWaiters';
import { isNotEmptyArray } from '@utils/functions';
import { appSelect, resetDateRange, setDateRange } from '@entities/app';
import * as S from './styles';
import { RangePickerProps } from 'antd/es/date-picker';

const RevenuePage: FC = () => {
    const { shop } = useSelector(authInfoSelect);
    const { from, to } = useSelector(appSelect).dateRange;
    const {
        tableCase,
        revenueRestaurants,
        revenueWaiters,
        selectedRestaurant,
    } = useSelector(rKeeperSelect);

    const dispatch = useDispatch<AppDispatch>();

    useEffect(() => {
        if (!shop) return;
        const params = {
            from: moment.utc(from).format(API_DATE_FORMAT),
            to: moment.utc(to).format(API_DATE_FORMAT),
        };

        if (tableCase === 0) {
            dispatch(getRevenueRestaurants({ shop_id: shop.id, params }));
        }

        if (tableCase === 1) {
            dispatch(getRevenueWaiters({ shop_id: shop.id, params }));
        }
    }, [shop, from, to, tableCase, dispatch]);

    useEffect(() => {
        if (shop) {
            dispatch(getRestaurants(shop.id));
        }
    }, [shop, dispatch]);

    useEffect(() => {
        if (shop && isNotEmptyArray(selectedRestaurant)) {
            const params: IRestTotalReq = {
                from: moment.utc(from).format(API_DATE_FORMAT),
                to: moment.utc(to).format(API_DATE_FORMAT),
                restaurant: selectedRestaurant
                    .map(r => r.id.toString())
                    .join(','),
            };
            dispatch(getRestTotal({ shop_id: shop.id, params }));
        }
    }, [shop, dispatch, selectedRestaurant, from, to]);

    const onDateChange: RangePickerProps['onChange'] = (date, dateStrings) => {
        if (!dateStrings[0] && !dateStrings[1] && !date) {
            dispatch(resetDateRange());
            return;
        }

        dispatch(
            setDateRange({
                from: dayjs(dateStrings[0], DISPLAY_DATE_FORMAT).format(
                    API_DATE_FORMAT
                ),

                to: dayjs(dateStrings[1], DISPLAY_DATE_FORMAT).format(
                    API_DATE_FORMAT
                ),
            })
        );
    };

    return (
        <S.Wrapper>
            <S.Header>
                <S.StyledHeadTitle>Выручка</S.StyledHeadTitle>
                <S.StyledCalendarWrapper>
                    <S.StyledRangePicker
                        suffixIcon={
                            <img
                                src={Icons.IconMainCalendar}
                                alt="icon calendar"
                            />
                        }
                        onChange={onDateChange}
                        value={[
                            dayjs(from, API_DATE_FORMAT),
                            dayjs(to, API_DATE_FORMAT),
                        ]}
                        format={DISPLAY_DATE_FORMAT}
                    />
                </S.StyledCalendarWrapper>
            </S.Header>
            <S.InfoWrapper>
                <HeaderRestaurantsInfo />
            </S.InfoWrapper>

            <div>
                {revenueRestaurants && (
                    <S.ChartsWrapper>
                        {tableCase === 0 ? (
                            <LineChartRestaurants
                                chartData={
                                    revenueRestaurants?.charts.restaurants
                                }
                            />
                        ) : (
                            revenueWaiters?.charts.waiters && (
                                <LineChartWaiters
                                    chartData={revenueWaiters?.charts.waiters}
                                />
                            )
                        )}
                    </S.ChartsWrapper>
                )}
                <TableHeaderRevenue />
                {tableCase === 0 ? (
                    <TableRevenueRestaurants />
                ) : (
                    <TableRevenueWaiters />
                )}
            </div>
        </S.Wrapper>
    );
};

export default RevenuePage;
